import ErrorComponent from "@/components/atoms/ErrorComponent";
import CompanyCourseHeader from "@/components/molecules/card-headers/companies/CompanyCourseHeader";
import CompanyCourseDetailsCard from "@/components/molecules/cards/companies/CompanyCourseDetailsCard";
import CompanyCourseInstructorsCard from "@/components/molecules/cards/companies/CompanyCourseInstructorsCard";
import CompanyCourseNotesCard from "@/components/molecules/cards/companies/CompanyCourseNotesCard";
import CompanyCourseUsersCard from "@/components/molecules/cards/companies/CompanyCourseUsersCard";
import CompanyDetailsCard from "@/components/molecules/cards/companies/CompanyDetailsCard";
import CompanyCoursePeopleCard from "@/components/molecules/cards/person/CompanyCoursePeopleCard";
import PreformatifDiagnosticSection from "@/components/molecules/cards/companies/PreformatifDiagnosticSection";
import { UUID_REGEX } from "@/constants/regexs";
import { pluralize } from "@/helpers/utils";
import CompanyCourse from "@/interfaces/CompanyCourse";
import {
  faBuilding,
  faFileLines,
  faMessage,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";
import { Card, Col, Nav, Row, Tab } from "react-bootstrap";
import { useParams } from "react-router-dom";
import SimpleBar from "simplebar-react";

const CompanyCourseView: FC = () => {
  const { id, courseId } = useParams<{ id: string; courseId: string }>();
  const [companyCourse, setCompanyCourse] = useState<CompanyCourse>();
  const [totals, setTotals] = useState({
    learners: 0,
    instructors: 0,
    notes: 0,
  });

  const onGetTotal = (key: string, total: number) => {
    setTotals((prev) => ({ ...prev, [key]: total }));
  };

  if (!id || !courseId || !UUID_REGEX.test(id) || !UUID_REGEX.test(courseId)) {
    return (
      <ErrorComponent error={{ status: 404, data: {} }} absolute returnButton />
    );
  }

  return (
    <div className="formalys-page formalys-detail-page">
      {companyCourse && (
        <CompanyCourseHeader
          id={id}
          secondId={courseId}
          courseId={companyCourse.courseId}
          companyCourse={companyCourse}
        />
      )}
      <Row className="g-3">
        <Col xxl={4} xl={5}>
          <CompanyCourseDetailsCard
            id={id}
            secondId={courseId}
            card
            callBack={setCompanyCourse}
          />
        </Col>
        {companyCourse && (
          <Col xxl={8} xl={7}>
            <Card>
              <Tab.Container defaultActiveKey={"company"}>
                <SimpleBar>
                  <Card.Header className="p-0 border-bottom">
                    <Nav className="nav-tabs border-0 flex-nowrap tab-contact-details">
                      <Nav.Item>
                        <Nav.Link
                          eventKey="company"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faBuilding}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">Entreprise</h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="users"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">Utilisateurs</h6>
                        </Nav.Link>
                      </Nav.Item>
                      {companyCourse.instructorId && (
                        <Nav.Item>
                          <Nav.Link
                            eventKey="instructor"
                            className="d-flex align-items-center py-3 px-x1 mb-0"
                          >
                            <FontAwesomeIcon
                              icon={faUser}
                              className="text-600 tab-icon"
                            />
                            <h6 className="text-600 mb-0 ms-1">Formateur</h6>
                          </Nav.Link>
                        </Nav.Item>
                      )}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="instructors"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Formateur", totals.instructors)}
                            &nbsp;(
                            {totals.instructors})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="learners"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Apprenant", totals.learners)}
                            &nbsp;(
                            {totals.learners})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="notes"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faMessage}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Note", totals.notes)}
                            &nbsp;(
                            {totals.notes})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="diagnostics"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faFileLines}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">Diagnostic</h6>
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Header>
                </SimpleBar>
                <Card.Body className="bg-light">
                  <Tab.Content>
                    <Tab.Pane eventKey="company">
                      <CompanyDetailsCard
                        id={companyCourse.companyId}
                        showDetailsButton
                      />
                    </Tab.Pane>
                    {companyCourse.ae && companyCourse.sdr && (
                      <Tab.Pane eventKey="users">
                        <CompanyCourseUsersCard
                          ae={companyCourse.ae}
                          sdr={companyCourse.sdr}
                          companyId={id}
                          courseId={courseId}
                        />
                      </Tab.Pane>
                    )}
                    <Tab.Pane eventKey="instructors">
                      <CompanyCourseInstructorsCard
                        courseId={courseId}
                        id={id}
                        onGetTotal={(total) => onGetTotal("instructors", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="learners">
                      <CompanyCoursePeopleCard
                        courseId={courseId}
                        id={id}
                        onGetTotal={(total) => onGetTotal("learners", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="notes">
                      <CompanyCourseNotesCard
                        companyId={id}
                        courseId={courseId}
                        onGetTotal={(total) => onGetTotal("notes", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="diagnostics">
                      <PreformatifDiagnosticSection
                        companyId={id}
                        courseId={courseId}
                      />
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Tab.Container>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CompanyCourseView;
